import {
	deleteAIMaskArtifactBundle,
	deleteAIMaskArtifactsForPhoto,
	deleteAIMaskArtifactsForProject,
	readAIMaskArtifactBundle,
	readAIMaskArtifactBundleMetadata,
	writeAIMaskArtifactBundle,
} from "./ai-mask-artifact-cache"
import {
	deleteMaskGroupThumbnail,
	deleteMaskGroupThumbnailsForPhoto,
	deleteMaskGroupThumbnailsForProject,
	readMaskGroupThumbnails,
	touchMaskGroupThumbnails,
	writeMaskGroupThumbnail,
} from "./mask-group-thumbnail-cache"
import type { Photo } from "@/core/photo"
import type { AIMaskArtifactKind } from "./ai-mask-artifact-types"
import { hashObject } from "@/utils/hash"

export interface MaskCacheContext {
	projectId: string
}

export type MaskGroupThumbnailWrite = Omit<
	Parameters<typeof writeMaskGroupThumbnail>[0],
	"projectId"
>

export interface MaskArtifactDescriptor {
	id: string
	projectId: string
	photoKey: string
	photoId: string
	kind: AIMaskArtifactKind
	pipelineSignature: string
	parentBundleId?: string
	personIndex?: number
}

export class MaskCache {
	// Persistence namespace derived from the project session; never application state.
	private projectId?: string

	setContext(context: MaskCacheContext) {
		this.projectId = context.projectId
	}

	clearContext() {
		this.projectId = undefined
	}

	createArtifactDescriptor(
		photo: Photo,
		kind: AIMaskArtifactKind,
		pipelineSignature: string,
		options: {
			settingsDigest?: string
			parentBundleId?: string
			personIndex?: number
		} = {},
	): MaskArtifactDescriptor | undefined {
		const projectId = this.projectId
		if (!projectId) {
			return
		}
		const id = hashObject({
			schemaVersion: 1,
			projectId,
			photoId: photo.id,
			initialOrientation: photo.initialOrientation,
			originalImageSize: [...photo.imageState.originalImageSize],
			kind,
			pipelineSignature,
			...options,
		})
		return {
			id,
			projectId,
			photoKey: `${projectId}:${photo.id}`,
			photoId: photo.id,
			kind,
			pipelineSignature,
			parentBundleId: options.parentBundleId,
			personIndex: options.personIndex,
		}
	}

	readArtifactBundle(id: string, roles?: string[]) {
		return readAIMaskArtifactBundle(id, roles)
	}

	readArtifactMetadata(id: string) {
		return readAIMaskArtifactBundleMetadata(id)
	}

	writeArtifactBundle(
		bundle: Parameters<typeof writeAIMaskArtifactBundle>[0],
		chunks: Parameters<typeof writeAIMaskArtifactBundle>[1],
	) {
		return writeAIMaskArtifactBundle(bundle, chunks)
	}

	deleteArtifact(id: string) {
		return deleteAIMaskArtifactBundle(id)
	}

	readMaskGroupThumbnails(photoId: string) {
		const projectId = this.projectId
		return projectId ? readMaskGroupThumbnails(projectId, photoId) : Promise.resolve([])
	}

	touchMaskGroupThumbnails(ids: string[]) {
		return touchMaskGroupThumbnails(ids)
	}

	writeMaskGroupThumbnail(input: MaskGroupThumbnailWrite) {
		const projectId = this.projectId
		return projectId
			? writeMaskGroupThumbnail({
					projectId,
					...input,
				})
			: Promise.resolve(false)
	}

	deleteMaskGroupThumbnail(photoId: string, groupId: string) {
		const projectId = this.projectId
		return projectId ? deleteMaskGroupThumbnail(projectId, photoId, groupId) : Promise.resolve()
	}

	async deletePhoto(projectId: string, photoId: string) {
		await Promise.all([
			deleteAIMaskArtifactsForPhoto(projectId, photoId),
			deleteMaskGroupThumbnailsForPhoto(projectId, photoId),
		])
	}

	async deleteProject(projectId: string) {
		await Promise.all([
			deleteAIMaskArtifactsForProject(projectId),
			deleteMaskGroupThumbnailsForProject(projectId),
		])
	}
}

export const MainMaskCache = new MaskCache()
